import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useSelector } from 'react-redux';
import { AdminColors } from '../Admin/constants/adminColors';
import FontFamily from '../../constants/fonts';
import PeopleHubHome from './PeopleHubHome';
import PeopleHubMyTeam, { EmployeeModal, EmpFormState, BLANK_FORM, buildEditFormFromMember } from './PeopleHubMyTeam';
import PeopleHubCoachingHistory from './PeopleHubCoachingHistory';
import PeopleHubHistory, { HistoryTabActivityRow } from './PeopleHubHistory';
import PeopleHubReports from './PeopleHubReports';
import BeginActivityOverlay from './BeginActivityOverlay';
import { ActivitySession } from './BeginActivity/PulseCheck';
import EmployeeProfileScreen from './EmployeeProfile';
import { PeopleHubTab, QueueItem, MyTeamMember, TeamMemberRosterStatus } from './types';
import type { EmployeeAllHistoryRow } from './EmployeeProfile/types';
import { updateEmployee, isExistingActivityResponse } from './peopleHubApi';
import { resolveAssigneeAndOwner } from './BeginActivity/common/activityPayload';
import { usePeopleHubSessionState } from './peopleHubSession';
import { useAppDispatch } from '../../store';
import { startPeopleHubActivity, resumePeopleHubActivity, setActiveSession } from '../../store/slices/peopleHubActivitySlice';
import { fetchEmployeeProfileData } from '../../store/slices/peopleHubSlice';

interface PeopleHubScreenProps {
  onBackPress?: () => void;
  /** Called when the user confirms an activity in the Begin Activity overlay.
   *  The session is stored in redux; App.tsx only needs to navigate. */
  onBeginActivity?: () => void;
  /** Called for Routine or Full Observation — navigates to the observation screen. */
  onBeginObservation?: () => void;
}

const TABS: { key: PeopleHubTab; label: string }[] = [
  { key: 'home',     label: 'Home' },
  { key: 'my-team',  label: 'My Team' },
  { key: 'coaching', label: 'Coaching History' },
  { key: 'history',  label: 'All History' },
  { key: 'reports',  label: 'Reports' },
];

const PeopleHubScreen: React.FC<PeopleHubScreenProps> = ({ onBackPress, onBeginActivity, onBeginObservation }) => {
  // Persisted for the browser-tab session: returning to People Hub (or refreshing) lands on the last viewed tab.
  const [activeTab, setActiveTab] = usePeopleHubSessionState<PeopleHubTab>('activeTab', 'home');
  const [isBeginActivityVisible, setIsBeginActivityVisible] = useState(false);
  const [focusEmployeeName, setFocusEmployeeName] = useState<string | null>(null);
  const [viewingEmployee, setViewingEmployee] = useState<MyTeamMember | null>(null);
  // Set only when Begin Activity is opened from the Employee Profile page — locks the
  // Employee field in the overlay while keeping Role (and Activity type) editable.
  const [beginActivityForEmployee, setBeginActivityForEmployee] = useState<MyTeamMember | null>(null);
  // Set when a My Queue task with no activity yet (activityId === null) is clicked — the overlay
  // is pre-filled from the task and the task's id is sent to /activities/start so no new task is created.
  const [beginActivityForTask, setBeginActivityForTask] = useState<QueueItem | null>(null);
  const [isEditEmployeeVisible, setIsEditEmployeeVisible] = useState(false);
  // Bumped after a successful profile edit so EmployeeProfileScreen re-fetches live data.
  const [profileRefreshToken, setProfileRefreshToken] = useState(0);
  const isMobile = useWindowDimensions().width < 1024;
  const dispatch = useAppDispatch();
  const selectedLocation = useSelector((state: any) => state.appData?.selectedLocation);
  const userInfo = useSelector((state: any) => state.appData?.userInfo);
  const fieldUsers = useSelector((state: any) => state.appData?.FieldUsers ?? []);
  const coeUsers = useSelector((state: any) => state.appData?.COEUsers ?? []);
  const employeesRaw = useSelector((state: any) => state.appData?.employees);
  const employeeRoster = useMemo(
    () => (Array.isArray(employeesRaw) ? employeesRaw : employeesRaw?.data ?? []),
    [employeesRaw],
  );

  // My Queue "open employee" navigates straight to the Employee Profile screen. The queue row
  // only carries name/id/role, so the rest of the profile fields are filled with placeholders
  // until a dedicated employee-lookup endpoint is wired in.
  const openEmployee = (employeeName: string, employeeId?: string, employeeRole?: string) => {
    // Employee Profile's breadcrumb reads "People Hub > My Team > {name}", so the tab
    // underneath must switch to My Team regardless of which tab the click came from.
    setActiveTab('my-team');
    setViewingEmployee({
      id: employeeId ?? employeeName,
      name: employeeName,
      gpid: employeeId ?? '—',
      manager: '—',
      role: employeeRole ?? '—',
      shift: '—',
      department: '—',
      org: '—',
      hireDate: '—',
      seniority: '—',
      seniorityOrder: 0,
      status: 'Active',
      cases: null,
      gapTime: null,
      cph: null,
      dps: null,
      bpa: null,
      ku: null,
      lastPulseCheck: null,
      lastObservation: null,
    });
  };

  // Resumes an existing in-progress Pulse Check / Observation queue row via
  // POST /people/activities (RESUME) so saved feedback/checklist/signatures can be mapped
  // back onto the screen once it mounts, instead of showing a blank draft.
  const openQueueActivity = async (item: QueueItem) => {
    // No activity exists for this task yet — start one (attached to this task) via the Begin Activity overlay.
    if (item.activityId == null) {
      setBeginActivityForTask(item);
      setIsBeginActivityVisible(true);
      return;
    }

    const baseSession = {
      employeeId: item.employeeId ?? item.id,
      employeeName: item.employeeName,
      role: item.employeeRole,
      employeeProfile: 'Tenured',
      // Not the employee's manager — `assignedTo` is who the queue task is assigned to.
      // The real manager comes from the employee profile fetch below (employee.manager),
      // which the activity hooks prefer over this field when building the New Action owner.
      managerName: item.assignedTo,
    };

    // Fetches the employee's last-12-weeks/periods metrics + coaching history so the
    // activity screen can render live data instead of mock rows. Fire-and-forget —
    // a failure here shouldn't block the resume flow.
    if (item.employeeId) {
      dispatch(fetchEmployeeProfileData({ employeeId: item.employeeId, clientTimestamp: new Date().toISOString() }));
    }

    if (item.type === 'Pulse Check') {
      try {
        const result = await dispatch(
          resumePeopleHubActivity({ taskId: item.id, activityType: 'PULSE_CHECK' })
        ).unwrap();
        dispatch(setActiveSession({
          ...baseSession,
          activityId: result.activityId,
          taskId: item.id,
          status: result.status,
          resumedFeedback: result.balance_feedback ?? result.feedback,
          resumedCoachingResult: result.coachingResult,
          resumedSupervisorFeedback: result.supervisorFeedback ?? result.coachingResult?.supervisorFeedback,
          resumedSignatures: result.signatures,
          signatures: result.signatures,
          isResumed: true,
          activityType: 'pulse_check',
        }));
        onBeginActivity?.();
      } catch (err) {
        console.warn('Failed to resume pulse check activity', err);
      }
      return;
    }

    if (item.type === 'Observation') {
      try {
        const result = await dispatch(
          resumePeopleHubActivity({ taskId: item.id, activityType: 'OBSERVATION' })
        ).unwrap();
        // The queue task's observationType is authoritative; the RESUME response is only a fallback.
        const activityType =
          (item.observationType ?? result.observationType) === 'Full' ? 'full_observation' : 'routine_observation';
        dispatch(setActiveSession({
          ...baseSession,
          activityId: result.activityId,
          taskId: item.id,
          status: result.status,
          stage: result.stage,
          checklistSelections: result.checklistSelections,
          checklistImages: result.images,
          customChecklist: result.customChecklist,
          resumedFeedback: result.balance_feedback ?? result.feedback,
          resumedCoachingResult: result.coachingResult,
          resumedSupervisorFeedback: result.supervisorFeedback ?? result.coachingResult?.supervisorFeedback,
          resumedSignatures: result.signatures,
          signatures: result.signatures,
          isResumed: true,
          activityType,
        }));
        onBeginObservation?.();
      } catch (err) {
        console.warn('Failed to resume observation activity', err);
      }
    }
  };

  // All History (Employee Profile card + All History tab): View/Resume re-opens the activity via
  // the same RESUME call the queue uses, keyed by activityId (history rows carry no taskId).
  // A COMPLETED activity opens read-only; anything still in progress opens editable.
  const resumeHistoryActivity = async (
    row: Pick<EmployeeAllHistoryRow, 'activityId' | 'rawActivityType' | 'observationType' | 'rawStatus'>,
    employee: { gpid: string; name: string; role: string; manager: string },
  ) => {
    const isPulseCheck = row.rawActivityType === 'PULSE_CHECK';
    const baseSession = {
      employeeId: employee.gpid,
      employeeName: employee.name,
      role: employee.role,
      employeeProfile: 'Tenured',
      managerName: employee.manager,
    };

    try {
      const result = await dispatch(
        resumePeopleHubActivity({
          activityId: row.activityId,
          activityType: isPulseCheck ? 'PULSE_CHECK' : 'OBSERVATION',
        })
      ).unwrap();
      const isReadOnly = (result.status ?? row.rawStatus ?? '').toUpperCase() === 'COMPLETED';

      if (isPulseCheck) {
        dispatch(setActiveSession({
          ...baseSession,
          activityId: result.activityId,
          status: result.status,
          resumedFeedback: result.balance_feedback ?? result.feedback,
          resumedCoachingResult: result.coachingResult,
          resumedSupervisorFeedback: result.supervisorFeedback ?? result.coachingResult?.supervisorFeedback,
          resumedSignatures: result.signatures,
          signatures: result.signatures,
          isResumed: true,
          isReadOnly,
          activityType: 'pulse_check',
        }));
        onBeginActivity?.();
      } else {
        const activityType =
          (result.observationType ?? row.observationType) === 'Full' ? 'full_observation' : 'routine_observation';
        dispatch(setActiveSession({
          ...baseSession,
          activityId: result.activityId,
          status: result.status,
          stage: result.stage,
          checklistSelections: result.checklistSelections,
          checklistImages: result.images,
          customChecklist: result.customChecklist,
          resumedFeedback: result.balance_feedback ?? result.feedback,
          resumedCoachingResult: result.coachingResult,
          resumedSupervisorFeedback: result.supervisorFeedback ?? result.coachingResult?.supervisorFeedback,
          resumedSignatures: result.signatures,
          signatures: result.signatures,
          isResumed: true,
          isReadOnly,
          activityType,
        }));
        onBeginObservation?.();
      }

      // Dispatched after navigating: leaving the profile screen resets the stored profile,
      // so fetching earlier would be wiped. The activity screen needs its metrics/coaching rows.
      if (employee.gpid) {
        dispatch(fetchEmployeeProfileData({ employeeId: employee.gpid, clientTimestamp: new Date().toISOString() }));
      }
    } catch (err) {
      console.warn('Failed to open activity from history', err);
    }
  };

  // Employee Profile > All History row.
  const openHistoryActivity = async (row: EmployeeAllHistoryRow) => {
    if (!viewingEmployee) return;
    await resumeHistoryActivity(row, {
      gpid: viewingEmployee.gpid,
      name: viewingEmployee.name,
      role: row.role || viewingEmployee.role,
      manager: viewingEmployee.manager,
    });
  };

  // All History tab row (raw /people/activities ActivityDTO).
  const openAllHistoryTabActivity = async (row: HistoryTabActivityRow) => {
    await resumeHistoryActivity(
      {
        activityId: row.id,
        rawActivityType: row.type,
        observationType: row.observationType ?? undefined,
        rawStatus: row.status,
      },
      {
        gpid: row.employeeId ?? '',
        name: row.employeeName,
        role: row.role || row.employeeRole || '—',
        manager: '—',
      },
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'home':
        return (
          <PeopleHubHome
            onOpenHistory={() => setActiveTab('history')}
            onOpenEmployee={openEmployee}
            onOpenQueueActivity={openQueueActivity}
          />
        );
      case 'my-team':
        return (
          <PeopleHubMyTeam
            focusEmployeeName={focusEmployeeName}
            onOpenEmployeeProfile={setViewingEmployee}
          />
        );
      case 'coaching':
        return <PeopleHubCoachingHistory onOpenEmployee={openEmployee} />;
      case 'history':
        return <PeopleHubHistory onOpenEmployee={openEmployee} onOpenActivity={openAllHistoryTabActivity} />;
      case 'reports':
        return <PeopleHubReports />;
      default:
        return <PeopleHubHome />;
    }
  };

  return (
    <View style={styles.container}>
      <View style={[styles.header, isMobile && styles.headerMobile]}>
        {!isMobile && (
          <View style={styles.breadcrumb}>
            {viewingEmployee ? (
              <>
                <TouchableOpacity onPress={() => { setViewingEmployee(null); setActiveTab('home'); }}>
                  <Text style={styles.breadcrumbLink}>People Hub</Text>
                </TouchableOpacity>
                <Text style={styles.breadcrumbSep}>›</Text>
                <TouchableOpacity onPress={() => setViewingEmployee(null)}>
                  <Text style={styles.breadcrumbLink}>My Team</Text>
                </TouchableOpacity>
                <Text style={styles.breadcrumbSep}>›</Text>
                <Text style={styles.breadcrumbCurrent}>{viewingEmployee.name}</Text>
              </>
            ) : (
              <>
                <TouchableOpacity onPress={onBackPress}>
                  <Text style={styles.breadcrumbLink}>Home</Text>
                </TouchableOpacity>
                <Text style={styles.breadcrumbSep}>›</Text>
                <Text style={styles.breadcrumbCurrent}>People Hub</Text>
              </>
            )}
          </View>
        )}
        <View style={styles.titleRow}>
          <View style={styles.titleTextGroup}>
            <Text style={styles.pageTitle}>People Hub</Text>
            <Text style={styles.pageSubtitle}>
              Operational warehouse roster for coaching, onboarding, attendance, and compliance.
            </Text>
          </View>
          <TouchableOpacity style={styles.beginButton} onPress={() => setIsBeginActivityVisible(true)}>
            <MaterialIcons name="add" size={16} color={AdminColors.textOnDark} />
            <Text style={styles.beginButtonText}>Begin Activity</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.tabBar}>
          {TABS.map((tab) => (
            <TouchableOpacity
              key={tab.key}
              onPress={() => { setViewingEmployee(null); setActiveTab(tab.key); }}
              style={[styles.tabItem, activeTab === tab.key && styles.tabItemActive]}
            >
              <Text style={[styles.tabLabel, activeTab === tab.key && styles.tabLabelActive]}>
                {tab.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
      <View style={styles.content}>
        {viewingEmployee ? (
          <EmployeeProfileScreen
            member={viewingEmployee}
            refreshToken={profileRefreshToken}
            onEdit={(member) => { setViewingEmployee(member); setIsEditEmployeeVisible(true); }}
            onBeginActivity={(member) => {
              setBeginActivityForEmployee(member);
              setIsBeginActivityVisible(true);
            }}
            onResumeActivity={openHistoryActivity}
            onOpenEmployee={openEmployee}
          />
        ) : (
          renderTabContent()
        )}
      </View>

      <BeginActivityOverlay
        visible={isBeginActivityVisible}
        onDismiss={() => {
          setIsBeginActivityVisible(false);
          setBeginActivityForEmployee(null);
          setBeginActivityForTask(null);
        }}
        presetEmployee={
          beginActivityForTask?.employeeId
            ? {
                employeeId: beginActivityForTask.employeeId,
                employeeName: beginActivityForTask.employeeName,
                role: beginActivityForTask.employeeRole,
              }
            : beginActivityForEmployee
              ? {
                  employeeId: beginActivityForEmployee.gpid,
                  employeeName: beginActivityForEmployee.name,
                  role: beginActivityForEmployee.role,
                }
              : undefined
        }
        presetActivityId={
          beginActivityForTask
            ? beginActivityForTask.type === 'Pulse Check'
              ? 'pulse_check'
              : beginActivityForTask.observationType === 'Routine'
                ? 'routine_observation'
                : 'full_observation'
            : undefined
        }
        allowedActivityIds={
          beginActivityForTask
            ? beginActivityForTask.type === 'Pulse Check'
              ? ['pulse_check']
              : beginActivityForTask.observationType
                ? [beginActivityForTask.observationType === 'Routine' ? 'routine_observation' : 'full_observation']
                : ['routine_observation', 'full_observation']
            : undefined
        }
        onProceed={async (data) => {
          const base = {
            employeeId:      data.employeeId,
            employeeName:    data.employee,
            role:            data.role,
            employeeProfile: 'Tenured',
            managerName:     data.managerName,
          };

          const isPulseCheck = data.activityType === 'Pulse Check';

          // Fetches the employee's last-12-weeks/periods metrics + coaching history so the
          // activity screen can render live data instead of mock rows. Fire-and-forget —
          // a failure here shouldn't block the start flow.
          if (data.employeeId) {
            dispatch(fetchEmployeeProfileData({ employeeId: data.employeeId, clientTimestamp: new Date().toISOString() }));
          }

          let startResult: import('../../screens/PeopleHub/peopleHubApi').StartActivityResponse;
          try {
            startResult = await dispatch(
              startPeopleHubActivity({
                activityType: isPulseCheck ? 'PULSE_CHECK' : 'OBSERVATION',
                observationType: isPulseCheck
                  ? undefined
                  : data.activityType === 'Routine Observation' ? 'Routine' : 'Full',
                employeeId: data.employeeId,
                locationId: selectedLocation?.id,
                locationName: selectedLocation?.name ?? selectedLocation?.displayText,
                locationSapId: selectedLocation?.sapId,
                role: data.role,
                // Attach the new activity to the clicked queue task instead of creating a new one.
                ...(beginActivityForTask ? { taskId: beginActivityForTask.id } : {}),
                // The new activity may be created as an Action Plan task, so send the
                // assignee (logged-in user) and owner (the employee's manager) up front.
                ...resolveAssigneeAndOwner({
                  userInfo,
                  ownerName: data.managerName,
                  directoryUsers: [...fieldUsers, ...coeUsers],
                  employeeRoster,
                }),
              })
            ).unwrap();
          } catch (err) {
            // Keep the overlay open so the user can retry.
            console.warn('Failed to start People Hub activity', err);
            return;
          }

          // START returns the already-open activity (RESUME-shaped) when one exists for this
          // employee + role + type — map its saved content onto the session like openQueueActivity does.
          const isExisting = isExistingActivityResponse(startResult);
          const sessionExtras = {
            activityId:   startResult.activityId,
            taskId:       startResult.taskId,
            status:       startResult.status,
            isAdHoc:      startResult.isAdHoc,
            fiscalYear:   startResult.fiscalYear,
            fiscalPeriod: startResult.fiscalPeriod,
            fiscalWeek:   startResult.fiscalWeek,
            stage:        startResult.stage,
            ...(isExisting
              ? {
                  checklistSelections: startResult.checklistSelections as Record<string, boolean> | undefined,
                  checklistImages: startResult.images,
                  customChecklist: startResult.customChecklist as any,
                  resumedFeedback: startResult.balance_feedback ?? startResult.feedback,
                  resumedCoachingResult: startResult.coachingResult,
                  resumedSupervisorFeedback:
                    startResult.supervisorFeedback ?? startResult.coachingResult?.supervisorFeedback,
                  resumedSignatures: startResult.signatures,
                  signatures: startResult.signatures,
                  isResumed: true,
                }
              : {}),
          };

          // Only close + redirect once the activity has started successfully.
          setIsBeginActivityVisible(false);
          setBeginActivityForEmployee(null);
          setBeginActivityForTask(null);

          if (isPulseCheck) {
            dispatch(setActiveSession({ ...base, ...sessionExtras, activityType: 'pulse_check' }));
            onBeginActivity?.();
          } else if (data.activityType === 'Routine Observation') {
            dispatch(setActiveSession({ ...base, ...sessionExtras, activityType: 'routine_observation' }));
            onBeginObservation?.();
          } else if (data.activityType === 'Full Observation') {
            dispatch(setActiveSession({ ...base, ...sessionExtras, activityType: 'full_observation' }));
            onBeginObservation?.();
          }
        }}
      />

      {viewingEmployee && (
        <EmployeeModal
          visible={isEditEmployeeVisible}
          mode="edit"
          initial={buildEditFormFromMember(viewingEmployee)}
          onClose={() => setIsEditEmployeeVisible(false)}
          onSubmit={async (form: EmpFormState) => {
            const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`;
            setViewingEmployee({
              ...viewingEmployee,
              name: fullName,
              gpid: form.gpid.trim(),
              manager: form.manager,
              role: form.role,
              shift: form.shift,
              department: form.department,
              org: form.organization,
              status: form.status as TeamMemberRosterStatus,
            });
            try {
              await updateEmployee(viewingEmployee.gpid, {
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                role: form.role,
                shift: form.shift,
                department: form.department,
                organization: form.organization,
                locationName: form.location,
                manager: form.manager,
                status: form.status,
                peerCoach: form.newHire ? form.peerCoach : undefined,
                preferredLanguage: form.preferredLanguage,
                newHire: form.newHire,
                hireDate: form.hireDate || undefined,
              });
              setProfileRefreshToken((t) => t + 1);
            } catch (err) {
              console.warn('Failed to save employee edit from profile page', err);
            }
          }}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: AdminColors.bgPage },
  header: {
    paddingHorizontal: 28,
    paddingTop: 20,
    paddingBottom: 0,
    backgroundColor: AdminColors.bgPage,
  },
  headerMobile: {
    paddingHorizontal: 14,
    paddingTop: 14,
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 12,
  },
  breadcrumbLink: { fontFamily: FontFamily.medium, color: AdminColors.textLink, fontSize: 14 },
  breadcrumbSep: { fontFamily: FontFamily.regular, color: AdminColors.textMuted, fontSize: 14 },
  breadcrumbCurrent: { fontFamily: FontFamily.medium, color: AdminColors.textSecondary, fontSize: 14 },
  titleRow: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 },
  titleTextGroup: { flex: 1, minWidth: 220, gap: 4 },
  pageTitle: { fontFamily: FontFamily.bold, fontSize: 22, color: AdminColors.textPrimary },
  pageSubtitle: { fontFamily: FontFamily.regular, fontSize: 13, color: AdminColors.textSecondary },
  beginButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: AdminColors.blue800,
    borderRadius: 24,
    paddingHorizontal: 18,
    paddingVertical: 10,
    flexShrink: 0,
  },
  beginButtonText: { fontFamily: FontFamily.medium, fontSize: 14, color: AdminColors.textOnDark },
  tabBar: {
    flexDirection: 'row',
    gap: 4,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
  },
  tabItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabItemActive: { borderBottomColor: AdminColors.blue700 },
  tabLabel: { fontFamily: FontFamily.medium, fontSize: 13, color: AdminColors.textSecondary },
  tabLabelActive: { color: AdminColors.blue700 },
  content: { flex: 1 },
});

export default PeopleHubScreen;
